import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TurmaFormDialog } from "@/components/forms/TurmaFormDialog";
import { TipoBadge } from "@/components/TipoBadge";
import { Plus, Trash2, GraduationCap, Pencil, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  ativa: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  planejada: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  encerrada: "bg-slate-500/20 text-slate-300 border-slate-500/40",
  finalizada: "bg-amber-500/20 text-amber-300 border-amber-500/40",
};

export default function Turmas() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [turmas, setTurmas] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selecionadas, setSelecionadas] = useState([]);
  const [dialog, setDialog] = useState(null); // {ids, preview} | null
  const [confirmTxt, setConfirmTxt] = useState("");
  const [excluirAlunos, setExcluirAlunos] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/turmas").then((r) => setTurmas(r.data));
  useEffect(() => {
    load();
    api.get("/cursos").then((r) => setCursos(r.data));
  }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (e, t) => { e.stopPropagation(); setEditing(t); setOpen(true); };

  const toggleSel = (e, id) => {
    e.stopPropagation();
    setSelecionadas((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const abrirExclusao = async (ids) => {
    setBusy(true); setConfirmTxt(""); setExcluirAlunos(false);
    try {
      const { data } = await api.post("/admin/limpeza/turmas/preview", { turma_ids: ids });
      setDialog({ ids, preview: data });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmarExclusao = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/limpeza/turmas", {
        turma_ids: dialog.ids, excluir_alunos_exclusivos: excluirAlunos, confirm: confirmTxt,
      });
      const x = data.excluidos;
      toast.success(`Limpeza concluída: ${x.turmas} turma(s), ${x.oficinas} oficina(s), ${x.avaliacoes} avaliação(ões), ${x.formativas} formativa(s), ${x.inscricoes} inscrição(ões), ${x.links} link(s), ${x.ocorrencias} ocorrência(s), ${x.alunos} aluno(s).`);
      setDialog(null); setSelecionadas([]); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cursosDisponiveis = isAdmin ? cursos : cursos.filter((c) => c.permite_aplicacoes_instrutor);
  const canCreate = isAdmin || cursosDisponiveis.length > 0;
  const emLote = selecionadas.length > 1;
  const p = dialog?.preview;

  return (
    <div>
      <button data-testid="voltar-btn" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <span aria-hidden>←</span> Voltar
      </button>
      <PageHeader title="Turmas" subtitle="Turmas vinculadas aos cursos">
        <div className="flex flex-wrap gap-2 justify-end">
          {isAdmin && selecionadas.length > 0 && (
            <Button variant="destructive" data-testid="excluir-selecionadas-btn" onClick={() => abrirExclusao(selecionadas)} disabled={busy}>
              <Trash2 className="h-4 w-4 mr-2" />Excluir selecionadas ({selecionadas.length})
            </Button>
          )}
          {canCreate && <Button data-testid="nova-turma-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Nova turma</Button>}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {turmas.map((t) => (
          <Card key={t.id} data-testid={`turma-${t.id}`} onClick={() => navigate(`/turmas/${t.id}`)}
            className={`p-5 bg-card border-border cursor-pointer hover:border-primary/60 hover:bg-card/70 transition-all ${selecionadas.includes(t.id) ? "ring-1 ring-red-500/50 border-red-500/40" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {isAdmin && (
                  <span onClick={(e) => toggleSel(e, t.id)} className="shrink-0">
                    <Checkbox data-testid={`sel-turma-${t.id}`} checked={selecionadas.includes(t.id)} className="pointer-events-none" />
                  </span>
                )}
                <div className="p-2 rounded-lg bg-purple-500/15"><GraduationCap className="h-5 w-5 text-purple-400" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-lg leading-tight break-words">{t.nome}</div>
                  <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                    <TipoBadge tipo={t.curso_tipo} className="shrink-0" />
                    <span className="text-xs text-muted-foreground truncate">{t.curso_nome}</span>
                  </div>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`edit-turma-${t.id}`} onClick={(e) => openEdit(e, t)} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`del-turma-${t.id}`} onClick={(e) => { e.stopPropagation(); abrirExclusao([t.id]); }} className="text-muted-foreground hover:text-red-400" title="Excluir turma e dados vinculados"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 mt-3">
              <Badge className={STATUS_BADGE[t.status] || "bg-secondary text-secondary-foreground border-border"}>{t.status}</Badge>
              {t.data_inicio && <span className="text-xs text-muted-foreground font-mono">{t.data_inicio} → {t.data_termino || "?"}</span>}
            </div>
            <div className="flex items-center gap-1 text-xs text-primary mt-3">Abrir turma <ChevronRight className="h-3.5 w-3.5" /></div>
          </Card>
        ))}
        {!turmas.length && <p className="text-muted-foreground text-sm">Nenhuma turma cadastrada.</p>}
      </div>

      <TurmaFormDialog open={open} onOpenChange={setOpen} turma={editing} onSaved={load} />

      <AlertDialog open={!!dialog} onOpenChange={(v) => { if (!v) setDialog(null); }}>
        <AlertDialogContent className="bg-popover border-border max-w-lg" data-testid="limpeza-turmas-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-400" />Excluir turma e dados vinculados</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>Esta ação é <b>definitiva</b> e não poderá ser desfeita. Serão removidos do banco:</p>
                {p && (
                  <div className="rounded-lg border border-border bg-secondary/20 p-3 font-mono text-xs space-y-1">
                    <div>Turmas: <b className="text-foreground">{p.totais.turmas}</b></div>
                    <div>Oficinas: <b className="text-foreground">{p.totais.oficinas}</b></div>
                    <div>Inscrições: <b className="text-foreground">{p.totais.inscricoes}</b></div>
                    <div>Links de inscrição / QR: <b className="text-foreground">{p.totais.links}</b></div>
                    <div>Avaliações práticas: <b className="text-foreground">{p.totais.avaliacoes}</b></div>
                    <div>Avaliações formativas: <b className="text-foreground">{p.totais.formativas}</b></div>
                    <div>Ocorrências: <b className="text-foreground">{p.totais.ocorrencias}</b></div>
                    <div>Presenças (capacitados): <b className="text-foreground">{p.totais.presencas}</b></div>
                    <div>Resultados: <b className="text-foreground">{p.totais.resultados}</b></div>
                    <div className="pt-1 border-t border-border">Alunos exclusivos: <b className="text-foreground">{p.alunos_exclusivos}</b> · Compartilhados preservados: <b className="text-foreground">{p.alunos_compartilhados_preservados}</b></div>
                  </div>
                )}
                {p && p.alunos_exclusivos > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                    <Checkbox data-testid="excluir-alunos-exclusivos" checked={excluirAlunos} onCheckedChange={setExcluirAlunos} />
                    <span>Também excluir os {p.alunos_exclusivos} aluno(s) exclusivos desta(s) turma(s)</span>
                  </label>
                )}
                <p className="text-xs text-muted-foreground">Modelos oficiais, instrução oficial e bancos institucionais são preservados.</p>
                <div>
                  <p className="mb-1">Para confirmar, digite <b>EXCLUIR</b>:</p>
                  <Input data-testid="confirm-excluir-input" value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)} placeholder="EXCLUIR" autoFocus />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="limpeza-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="limpeza-confirmar" disabled={confirmTxt.trim().toUpperCase() !== "EXCLUIR" || busy}
              onClick={(e) => { e.preventDefault(); confirmarExclusao(); }} className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
